import { useEffect, useState, forwardRef, useRef } from 'react';
import ApplyButton from './ApplyButton';
import Badge from '../Badge/Badge';
import { Items, SelectMenusProps } from './Types';
import { Menu } from './Menu';
import { MenuHeader } from './MenuHeader';
import { cn } from '../../utils';

export default forwardRef(function SelectMenus<T extends Items = Items>(
    {
        label,
        error = '',
        disabled = false,
        items,
        placeholder = '',
        multiSelectVariant = 'primary',
        textVariant = 'primary',
        doubleLine = false,
        infoComponent,
        applyButton = false,
        multiSelect = false,
        badges = false,
        searchInput = false,
        leadingIcon = false,
        loading = false,
        selectAll = false,
        className = '',
        onSelect,
        defaultValues = multiSelect ? [] : null,
        children,
        menuChildren,
        openMenu,
        disableOutsideClick = false,
        allowClear = false,
        ...rest
    }: SelectMenusProps<T>,
    ref: React.Ref<HTMLDivElement>,
) {
    const [open, setOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<T[] | Items[]>([]);
    const [selectedApplyItems, setSelectedApplyItems] = useState<T[] | Items[]>(
        [],
    );
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [errorTerm, setErrorTerm] = useState<string>(error);
    const [selectsAll, setSelectAll] = useState(false);
    const [clearSelection, setClearSelection] = useState(false);
    const isObject = items !== undefined && typeof items[0] === 'object';
    const isOpenMenu = openMenu !== undefined ? openMenu : open;

    const selectMenuRef = useRef<HTMLDivElement | null>(null);

    // convert array to array of object
    const normalizedItems = items?.map((item, index): T | Items =>
        typeof item === 'object' && item !== null
            ? item
            : { title: String(item), id: index + 1 },
    );

    const filteredItems = normalizedItems?.filter(item => {
        const searchTermLower = searchTerm.toLowerCase();
        const titleMatch = item.title.toLowerCase().includes(searchTermLower);

        if (doubleLine) {
            const idMatch = item.id
                .toString()
                .toLowerCase()
                .includes(searchTermLower);
            return titleMatch || idMatch;
        }

        return titleMatch;
    });

    const enhancedFilteredItems = [
        {
            title: 'Select All/Unselect All',
            id: '',
        },
        ...(filteredItems || []),
    ];

    function getItemsByTitle(title: string | string[]): T[] | Items[] | false {
        const matchedItems = normalizedItems?.filter(item =>
            Array.isArray(title)
                ? title.includes(item.title)
                : item.title === title,
        );

        return matchedItems && matchedItems.length > 0 ? matchedItems : false;
    }

    useEffect(() => {
        if (defaultValues === 'clear') {
            setSelectedItems([]);
            setSelectedApplyItems([]);
            return;
        }

        if (!defaultValues || !Array.isArray(defaultValues)) return;

        const result = isObject
            ? defaultValues
            : getItemsByTitle(defaultValues as string | string[]);

        if (isObject && result !== false && Array.isArray(defaultValues)) {
            setSelectedItems(defaultValues as T[]);
            setSelectedApplyItems(defaultValues as T[]);
        } else if (Array.isArray(result)) {
            setSelectedItems(result as T[]);
            setSelectedApplyItems(result as T[]);
        }
    }, [defaultValues ? JSON.stringify(defaultValues) : null, items?.length]);

    useEffect(() => {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
        }
    }, [open]);

    useEffect(() => {
        if (clearSelection) {
            setSelectedItems([]);
            setSelectedApplyItems([]);
            setOpen(false);
            onSelect([]);
            setClearSelection(false);
        }
    }, [clearSelection, selectedApplyItems]);

    useEffect(() => {
        setErrorTerm(error);
        if (error) {
            setOpen(false);
            setSelectedItems([]);
        }
    }, [error]);

    const handleSelectAllToggle = () => {
        const selected = selectsAll ? [] : filteredItems || [];
        if (applyButton) {
            setSelectedApplyItems(selected);
        } else {
            setSelectedItems(selected);
        }
        setSelectAll(!selectsAll);
    };

    const toggleSelectItem = (item: T | Items) => {
        if (disabled) return;

        if (item.title === 'Select All/Unselect All') {
            handleSelectAllToggle();
            return;
        }

        const isApplyButtonAndOpen = applyButton && open;

        const items = isApplyButtonAndOpen ? selectedApplyItems : selectedItems;
        setErrorTerm('');
        if (items.some(selectedItem => selectedItem.id === item.id)) {
            const unselectedItems = multiSelect
                ? items.filter(selectedItem => selectedItem.id !== item.id)
                : [];
            !isApplyButtonAndOpen && setSelectedItems(unselectedItems);
            !multiSelect && setOpen(false);
            setSelectedApplyItems(unselectedItems);

            if (isObject) {
                onSelect(unselectedItems);
                return;
            }

            onSelect(unselectedItems.map(item => item.title));
        } else {
            const selectedItems = multiSelect ? [...items, item] : [item];
            if (!isApplyButtonAndOpen) {
                setSelectedItems(selectedItems);
                !multiSelect && setOpen(false);
            }
            setSelectedApplyItems(selectedItems);

            if (isObject) {
                onSelect(selectedItems);
                return;
            }

            onSelect(selectedItems.map(item => item.title));
        }
    };

    const isSelected = (item: T): boolean => {
        if (applyButton) {
            return selectedApplyItems.some(
                selectedApplyItem => selectedApplyItem.id === item.id,
            );
        }
        return selectedItems.some(selectedItem => selectedItem.id === item.id);
    };

    useEffect(() => {
        if (disableOutsideClick) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                selectMenuRef.current &&
                !selectMenuRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [disableOutsideClick]);

    return (
        <div
            ref={ref ? ref : selectMenuRef}
            className={cn('alli-relative alli-flex alli-flex-col', className, {
                'alli-pointer-events-none alli-opacity-50': disabled,
            })}
            data-testid="select-menus">
            {(label || infoComponent) && (
                <div
                    className={cn(
                        'alli-mb-2 alli-flex alli-gap-3 alli-text-sm alli-font-medium',
                        disabled
                            ? 'alli-text-gray-600'
                            : 'alli-text-blue-gray-700',
                    )}>
                    {label}
                    {infoComponent && infoComponent}
                </div>
            )}

            <MenuHeader<T>
                open={open}
                error={errorTerm}
                disabled={disabled}
                searchInput={searchInput}
                setOpen={setOpen}
                doubleLine={doubleLine}
                setSearchTerm={setSearchTerm}
                placeholder={placeholder}
                selectedItems={selectedItems}
                leadingIcon={leadingIcon}
                multiSelectVariant={multiSelectVariant}
                multiSelect={multiSelect}
                className={className}
                setClearSelection={setClearSelection}
                allowClear={allowClear}
                {...rest}
                textVariant={textVariant}>
                {open && !disabled && isOpenMenu && (
                    <div className="alli-absolute alli-right-0 alli-top-full alli-z-20 alli-mt-2 alli-flex alli-max-h-[298px] alli-w-full alli-flex-col alli-truncate alli-border alli-border-gray-400 alli-bg-white alli-py-1 alli-shadow-lg sm:alli-rounded-lg">
                        <div className="alli-flex-grow alli-overflow-y-auto">
                            <Menu
                                filteredItems={
                                    selectAll && multiSelect
                                        ? enhancedFilteredItems
                                        : filteredItems
                                }
                                toggleSelectItem={toggleSelectItem}
                                isSelected={isSelected}
                                doubleLine={doubleLine}
                                leadingIcon={leadingIcon}
                                loading={loading}
                                selectAll={selectAll}
                                textVariant={textVariant}
                            />
                        </div>
                        {menuChildren}
                        {applyButton && (
                            <ApplyButton
                                setSelectedItems={setSelectedItems}
                                setSelectedApplyItems={setSelectedApplyItems}
                                setOpen={setOpen}
                                selectedApplyItems={selectedApplyItems}
                                onSelect={onSelect}
                            />
                        )}
                    </div>
                )}
            </MenuHeader>

            {errorTerm && (
                <div className="alli-mt-2 alli-text-sm alli-font-normal alli-text-red-400">
                    {errorTerm}
                </div>
            )}
            {children}
            {badges && (
                <div className="alli-mt-3 alli-flex alli-w-full alli-flex-wrap alli-gap-1.5">
                    {selectedItems.map((item, index) => (
                        <Badge
                            key={index}
                            variant={disabled ? 'gray' : 'blue'}
                            onClick={() => {
                                toggleSelectItem(item);
                            }}>
                            {item.title}
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
});
